import { Alert } from '@mui/material';
import { useMemo, useState } from 'react';
import ReactPaginate from 'react-paginate';
import styles from 'src/pages/accueil/styles/style-setting.module.scss';
import ProductivityTableColumns, { ProductivityTableRow } from './productivity-table-columns';

const DEFAULT_PAGE_SIZE = 15;

type ProductivityTableProps = {
  t: (key: string) => string;
  productivites: any[];
  unitProductionName?: string;
  showEditAction?: boolean;
  editableQuantities?: boolean;
  onEditProductivity?: (item: any, globalIndex: number) => void;
  onQuantityChange?: (globalIndex: number, value: number | undefined) => void;
  pageSize?: number;
  emptyMessageKey?: string;
};

const ProductivityTable = ({
  t,
  productivites = [],
  unitProductionName = '',
  showEditAction = false,
  editableQuantities = false,
  onEditProductivity,
  onQuantityChange,
  pageSize = DEFAULT_PAGE_SIZE,
  emptyMessageKey = 'production_cycle.no_productivity',
}: ProductivityTableProps) => {
  const [search, setSearch] = useState('');
  const [pageNumber, setPageNumber] = useState(0);

  const filteredEntries = useMemo(() => {
    const needle = search.trim().toUpperCase();
    return productivites
      .map((prod: any, globalIndex: number) => ({ prod, globalIndex }))
      .filter(({ prod }) => {
        if (!needle) return true;
        const q = prod?.quantity != null ? String(prod.quantity) : '';
        const sync = prod?.idSynchro || '';
        return `${q} ${sync}`.toUpperCase().includes(needle);
      });
  }, [productivites, search]);

  const pagesVisited = pageNumber * pageSize;
  const pageCount = Math.ceil(filteredEntries.length / pageSize);

  const rows: ProductivityTableRow[] = useMemo(() => {
    return filteredEntries.slice(pagesVisited, pagesVisited + pageSize).map(({ prod, globalIndex }) => {
      const qty = prod?.quantity;
      return {
        quantitySort: qty ?? Number.NEGATIVE_INFINITY,
        quantityRaw: qty,
        unitName: unitProductionName,
        globalIndex,
        original: prod,
      };
    });
  }, [filteredEntries, pagesVisited, pageSize, unitProductionName]);

  return (
    <div>
      <input
        className="form-control mb-2"
        value={search}
        onChange={(event) => {
          setSearch(event.target.value);
          setPageNumber(0);
        }}
        placeholder={t('search_unit')}
      />

      {rows.length === 0 ? (
        <Alert variant="outlined" severity="info">
          {t(emptyMessageKey)}
        </Alert>
      ) : (
        <ProductivityTableColumns
          t={t}
          data={rows}
          showEditAction={showEditAction}
          editableQuantities={editableQuantities}
          onEditProductivity={onEditProductivity}
          onQuantityChange={onQuantityChange}
        />
      )}

      {filteredEntries.length > pageSize && (
        <div className="d-flex justify-content-end mt-3">
          <ReactPaginate
            previousLabel="<"
            nextLabel=">"
            pageCount={pageCount}
            onPageChange={({ selected }) => setPageNumber(selected)}
            forcePage={pageNumber}
            containerClassName={styles['paginationButton']}
            previousLinkClassName={styles['previousBtn']}
            nextLinkClassName={styles['nextBtn']}
            disabledClassName={styles['paginationDisabled']}
            activeClassName={styles['paginationActive']}
          />
        </div>
      )}
    </div>
  );
};

export default ProductivityTable;
